import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import { AutocompleteInput, ReferenceInput, required, useDataProvider } from 'react-admin';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import { handleAPIErrors, suggestionsContainerProps } from '../utils';

const styles = {
  fullWidth: {
    width: '100%',
  },
  linkToDesignCenter: {
    textTransform: 'none',
    textDecoration: 'underline',
    color: '#00A1A1',
    padding: '15px 20px',
    fontSize: '16px',
    cursor: 'pointer',
  },
};

export const setWebsiteId = (websiteId) => {
  if (typeof websiteId === 'undefined') {
    return '';
  }

  return websiteId.includes('websites') ? websiteId : `/websites/${websiteId}`;
};

export const getTemplateId = (savedTemplateId, selectedTemplates, formName) => {
  return savedTemplateId || selectedTemplates[formName].id;
};

const getSource = (formName) => {
  switch (formName) {
    case 'pop-up':
      return 'popUpTemplate';
    case 'declaration':
      return 'declarationTemplate';
    case 'choice-box':
      return 'choiceBoxTemplate';
  }
};

function TemplatesSelectionDialog(props) {
  const { classes, templatesType, selectedTemplates, formName } = props;

  const [open, setOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [templates, setTemplates] = useState([]);
  const [selectedValue, setSelectedValue] = useState(selectedTemplates[getSource(formName)]);
  const [savedTemplateId, setSavedTemplateId] = useState('');
  const [account, setAccount] = useState(null);
  const dataProvider = useDataProvider();


  useEffect(() => {
    getTemplates(props.templatesType);
  }, [props.templatesType]);

  /**
   * Available templates types are: 'pop_up_templates', 'declaration_templates', 'choice_box_templates'.
   * @param templatesType
   * @returns {boolean}
   */
  const getTemplates = (templatesType) => {
    const websiteId = setWebsiteId(props.website);

    dataProvider.getOne('websites', { id: websiteId }).then(websiteResponse => {
      setAccount(websiteResponse.data.account);

      return dataProvider.getList(templatesType, {
        pagination: {
          page: 1,
        },
        sort: {
          field: 'account.id',
          order: 'DESC',
        },
        filter: {
          accounts: websiteResponse.data.account,
          itemsPerPage: 1000,
        },
      });
    }).then(templatesResponse => {
      setTemplates(templatesResponse.data);
    }).catch(error => {
      handleAPIErrors(error, props.history);
      console.error(error);
    });
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);

    const selectedTemplateId = getTemplateId(savedTemplateId, selectedTemplates, getSource(formName));

    dataProvider.getOne(props.templatesType, { id: selectedTemplateId })
      .then(response => {
        fillForm(response.data);
      });
  };

  const handleUpdate = () => {
    setOpen(false);
    dataProvider.getOne(props.templatesType, { id: selectedValue })
      .then(response => {
        setSavedTemplateId(response.data.id);
        fillForm(response.data);
        props.callbackFromParent(response.data.name);
      });
  };

  const handleTemplateChange = (value) => {
    setSelectedValue(value);
  };

  const fillForm = (valuesToFill) => {
    props.onSubmit(valuesToFill);
  };

  return (
    <div className={props.style}>
      <Button variant="outlined" data-testid="change-template-button" color="primary" onClick={handleClickOpen}>
        Change template
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          Select template
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to override the current design.
            The new design will be applied to all domains within this Consent Solution.
          </DialogContentText>
          <ReferenceInput className={classes.fullWidth}
            label="Template" source={getSource(formName)}
            reference={templatesType}
            filterToQuery={searchText => ({ name: searchText })}
            filter={{ accounts: account, itemsPerPage: 1000 }}
            sort={{ field: 'account.id', order: 'DESC' }}
            onChange={handleTemplateChange}
            format={v => typeof (v) === 'object' ? v.id : v}
            validate={required()}>
            <AutocompleteInput options={{ suggestionsContainerProps }} optionValue="id" optionText={choice => choice.account == null ?
              `${choice.name} ---------------------------- DEFAULT` :
              `${choice.name} ------------------------------ CUSTOM`} allowEmpty/>
          </ReferenceInput>
        </DialogContent>
        <Button href="http://templates.cookieinformation.com"
          target="_blank"
          className={classes.linkToDesignCenter}>
          Click here to see all designs in our Design Center (will open in a new tab)
        </Button>
        <DialogActions>
          <Button onClick={handleClose} data-testid="select-template-cancel-button" color="primary">
            Cancel
          </Button>
          <Button onClick={handleUpdate} data-testid="select-template-update-button" color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

const StyledTemplatesSelectionDialog = withStyles(styles)(TemplatesSelectionDialog);

export default StyledTemplatesSelectionDialog;
